import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Plain-English labels mapped over the technical names the runtime
// uses. The runtime keeps its precise terminology (task_type values,
// evaluator function names, tool names) so the developer/eval-harness
// path stays unambiguous; the BUILDER shows MEP-contractor friendly
// labels so a foreman or PM doesn't need to think in code.

const TASK_TYPE_LABELS = {
  extraction:     'Pull data out of a file',
  estimation:     'Estimate labor or cost',
  compliance:     'Check against a code or spec',
  submittal:      'Review a submittal',
  drawing_review: 'Review drawings',
  custom:         'Something else',
};

const EVALUATOR_LABELS = {
  rowCountRatioEvaluator:     'Count check (did we extract every row we expected?)',
  citationCoverageEvaluator:  'Citation check (does every finding name its source page + code clause?)',
  checklistCoverageEvaluator: 'Checklist check (is every required item accounted for?)',
  sheetRefCoverageEvaluator:  'Drawing check (does every finding cite a sheet # + location?)',
  customPromptEvaluator:      'Custom check — you write the rule in plain English',
};

/**
 * Task-type-specific starter templates. The user picks a task type and
 * the form auto-fills with a realistic MEP-contractor example for that
 * job — name, description, persona, one full Play, suggested queries,
 * and an appropriate tool subset. Editing any field overrides the
 * template; switching task type re-applies the new template.
 *
 * Each template is intentionally complete enough that a user could
 * hit "Save & try it out" within seconds.
 */
const TEMPLATES = {
  extraction: {
    name: 'PDF Takeoff Extractor',
    description: 'Pulls every material line from a PDF takeoff into a clean Excel.',
    personaMd:
`## What this agent does
Reads a PDF material takeoff and produces an Excel with every line item — description, quantity, unit, and any spec callouts.

## Must-follow rules
- Don't skip rows. If the source has 500 line items, the output has 500 line items.
- Preserve the original units (LF, EA, SF) exactly — never convert.
- If a row has a spec callout (e.g. "per detail 5/M-301"), keep it in its own column.
- If a row is illegible or partially OCR'd, mark its Confidence column "low" — don't guess.`,
    strategies: [{
      name: 'Read every page in order',
      when: 'Any takeoff PDF where the row layout is consistent.',
      steps: `Get the document's page count and table of contents
Read each page in order
Parse each line into Description / Qty / Unit / Spec callout columns
For long documents, use the bulk Python parser instead of one-page-at-a-time`,
      exit: 'Every line in the source PDF appears in the Excel; no rows dropped.',
    }],
    tools: ['pdf_info', 'pdf_search', 'pdf_read_pages', 'pdf_profile',
            'file_info', 'file_read',
            'start_extraction', 'add_rows', 'finalize_extraction',
            'run_python_extract', 'run_python_on_file', 'evaluate_extraction',
            'generate_excel'],
    suggestedQueries: [
      'Extract every row of this takeoff into Excel.',
      'Convert this PDF schedule to a clean spreadsheet.',
      'Pull the fixture list out of this submittal as a table.',
    ],
  },

  estimation: {
    name: 'Labor Hour Estimator',
    description: 'Adds MCAA or NECA labor hours to every line of an uploaded BOM.',
    personaMd:
`## What this agent does
Takes a Bill of Materials, looks up labor rates (MCAA for mechanical/piping, NECA for electrical), and produces an Excel with the original rows plus Labor Hrs/Unit, Total Labor Hrs, and Match Description columns.

## Must-follow rules
- NEVER invent labor hours. If a row has no match in MCAA/NECA, write "No match" — do not use industry-average guesses.
- For mixed BOMs, route each row to the right table: electrical (EMT, GRC, wire, panels, fixtures) → NECA; mechanical / piping / sheet metal / insulation → MCAA.
- Total Labor Hrs = Qty × Labor Hrs/Unit, written as an Excel formula (=B5*F5), not a static number.
- Add a TOTAL row at the bottom of each sheet summing the Total Labor Hrs column.`,
    strategies: [{
      name: 'Route by trade, then look up labor',
      when: 'Any BOM, takeoff, or material list with quantities and units.',
      steps: `Extract every material row (Description, Qty, Unit)
For each row, decide the trade (electrical = NECA, mechanical/piping = MCAA)
Look up labor rates in the right table
Append Labor Hrs/Unit, Total Labor Hrs, and Match Description columns
Add a TOTAL row at the bottom`,
      exit: 'Every line has Labor Hrs/Unit OR "No match", and the TOTAL row sums to a positive number.',
    }],
    tools: ['pdf_info', 'pdf_search', 'pdf_read_pages', 'pdf_profile',
            'file_info', 'file_read',
            'start_extraction', 'add_rows', 'finalize_extraction',
            'run_python_extract', 'run_python_on_file', 'evaluate_extraction',
            'batch_estimate_labor_cost', 'batch_estimate_neca_labor_cost',
            'generate_excel'],
    suggestedQueries: [
      'Estimate labor for this BOM.',
      'Apply MCAA rates to this piping takeoff.',
      'Apply NECA labor units to every line of this electrical BOM.',
    ],
  },

  compliance: {
    name: 'Code Compliance Checker',
    description: 'Reviews submittals or drawings against a code/spec section with cited findings.',
    personaMd:
`## What this agent does
Reads a submittal or drawing set and checks it against a specific code or spec section the user names (NEC, NFPA, ASHRAE, IBC, or a project spec section like 23 05 00). Produces a finding list — each row has both the document page citation and the specific code clause citation.

## Must-follow rules
- Every finding must cite BOTH (1) the document page AND (2) the specific code clause (e.g. "NEC 250.4(A)(1)").
- Never declare something "compliant" without citing the clause that says so.
- If a clause has an exception, surface it explicitly.
- If a topic isn't covered by the named code/spec, label it "out of scope" — do not extrapolate.
- If the user didn't name a specific code or spec section, ask once before proceeding.`,
    strategies: [{
      name: 'Search → read → classify',
      when: 'User names a code or spec section to check against.',
      steps: `Identify the code/standard the user named (NEC, NFPA, ASHRAE, IBC, spec section)
Search the document for terms from that code's key clauses
Read the surrounding pages for context
For each requirement: classify as compliant / non-compliant / not-applicable
Write down both the document page AND the specific clause for every finding`,
      exit: 'Every finding has both a document page citation and a specific code clause citation.',
    }],
    tools: ['pdf_info', 'pdf_search', 'pdf_read_pages', 'pdf_profile',
            'file_info', 'file_read', 'evaluate_extraction',
            'list_aec_mcp_tools', 'call_aec_mcp_tool',
            'generate_word'],
    suggestedQueries: [
      'Check this submittal against NEC 250 grounding requirements.',
      'Does this riser diagram meet ASHRAE 90.1?',
      'Is the panel schedule compliant with NEC Article 408?',
    ],
  },

  submittal: {
    name: 'Submittal Reviewer',
    description: 'Checks an MEP submittal against its spec section\'s required components.',
    personaMd:
`## What this agent does
Compares a submittal package against the spec section it claims to satisfy and produces a checklist. Every required component (product data, shop drawings, samples, certifications, O&M data, warranties, mockups, test reports) is marked PRESENT with a page citation or MISSING with an action item.

## Must-follow rules
- For each PRESENT item, cite the page in the submittal where it appears.
- For each MISSING item, name the responsible party (GC / sub / manufacturer / vendor).
- If the spec section number wasn't provided, ask once — then proceed once the user picks one.
- Don't mark a submittal "complete" unless every required component is verified present.`,
    strategies: [{
      name: 'Spec → checklist → match',
      when: 'User uploads a submittal and names (or implies) the spec section.',
      steps: `Find the spec section in the project documents (or use the named code)
Enumerate every required component (product data, shop drawings, samples, certs, O&M, warranty, mockups, test reports)
Scan the submittal for each component
Mark each PRESENT with page # or MISSING with the responsible party
Summarize what's needed to complete the submittal`,
      exit: 'Every spec-required component is classified PRESENT (with page) or MISSING (with action).',
    }],
    tools: ['pdf_info', 'pdf_search', 'pdf_read_pages', 'pdf_profile',
            'file_info', 'file_read', 'evaluate_extraction',
            'generate_word', 'generate_excel'],
    suggestedQueries: [
      'Review this HVAC submittal against spec section 23 05 00.',
      'What\'s missing from this fire alarm submittal?',
      'Compare this submittal to the spec — what still needs to be provided?',
    ],
  },

  drawing_review: {
    name: 'Drawing Reviewer',
    description: 'Flags notable items, RFI candidates, and changes across a drawing set.',
    personaMd:
`## What this agent does
Scans a drawing set (or compares two revisions) and produces a list of notable items: "BY OTHERS" callouts, "NIC" exclusions, "VIF" items, future-work boxes, revision markers, and scope-change indicators. Each finding cites the sheet number and a specific location.

## Must-follow rules
- Every finding must cite BOTH (1) the sheet number AND (2) a specific location (grid intersection, room number, or detail #).
- Classify each finding: RFI-candidate / as-built / dim-change / scope-change.
- For revision comparisons: list what changed by sheet, not just "drawings updated."
- Never invent sheet numbers — only cite what's actually visible in the title block.`,
    strategies: [{
      name: 'Search → read → classify findings',
      when: 'User uploads drawing PDFs and wants notable items or revision diffs.',
      steps: `Get the sheet list from the cover sheet or table of contents
Search the drawings for callout terms: BY OTHERS, NIC, VIF, FUTURE, TYP, REVISION
Read the pages around each match for context
For each finding, record sheet # + grid/room/detail + classification
Group findings by sheet in the output`,
      exit: 'Every finding has both a sheet number and a specific location, with a classification.',
    }],
    tools: ['pdf_info', 'pdf_search', 'pdf_read_pages', 'pdf_ocr_pages', 'pdf_profile',
            'file_info', 'file_read', 'evaluate_extraction',
            'generate_word'],
    suggestedQueries: [
      'Find every NIC / BY OTHERS callout in this drawing set.',
      'What changed between revision 1 and revision 2?',
      'Flag all VIF items and the sheets they\'re on.',
    ],
  },
};

const TOOL_LABELS = {
  pdf_info:           'See a PDF\'s page count + table of contents',
  pdf_search:         'Search a PDF for words/phrases',
  pdf_read_pages:     'Read a specific page range from a PDF',
  pdf_ocr_pages:      'OCR scanned/image PDF pages',
  pdf_profile:        'Auto-detect what kind of document this is',
  file_info:          'See an Excel/Word/PPT file\'s sheets, headings, etc.',
  file_read:          'Convert any file to plain text',
  start_extraction:   'Start a row-by-row data collection job',
  add_rows:           'Add rows to an ongoing extraction',
  finalize_extraction:'Finish a job and produce the Excel download',
  run_python_extract: 'Run a Python parser on a PDF (fast, for huge takeoffs)',
  run_python_on_file: 'Run a Python parser on Excel/Word/PPT/CSV',
  evaluate_extraction:'Self-evaluate the output before declaring done',
  generate_excel:     'Generate a formatted Excel file',
  generate_word:      'Generate a Word document',
  generate_pdf:       'Generate a PDF report',
  generate_dashboard: 'Generate an HTML dashboard with charts',
  batch_estimate_labor_cost: 'Apply MCAA labor rates to a list of items',
  batch_estimate_neca_labor_cost: 'Apply NECA labor rates (electrical) to a list',
  estimate_labor_cost: 'Look up MCAA labor for a single item',
  estimate_neca_labor_cost: 'Look up NECA labor for a single electrical item',
  list_aec_mcp_tools: 'List available industry data sources',
  call_aec_mcp_tool:  'Look up industry data (BLS wages, FHWA, etc.)',
};

/**
 * RsiAgentBuilder — single-page form to author a new RSI agent.
 *
 * Design principles:
 *   - Menu-driven (no custom code execution). Users pick evaluators
 *     from a 4-option dropdown, tools from a multi-select whitelist,
 *     task type from a 5-radio group. The platform's safety boundary
 *     is enforced both in the UI (only valid options shown) and on
 *     the server (validated again before insert).
 *   - Progressive disclosure. Three sections collapse independently
 *     so a user can fill them in order without overwhelm.
 *   - Inline help. Every section has a sentence explaining what it
 *     does so a first-time builder doesn't need to read the docs.
 *
 * On save, POSTs to /api/rsi-agents. The server immediately registers
 * the agent in the in-process runtime; subsequent server restarts
 * rehydrate from the DB.
 */
export default function RsiAgentBuilder() {
  const navigate = useNavigate();
  // /rsi-agents/new (no :id)        → create
  // /rsi-agents/:id/edit            → edit existing (owner only)
  // The latter pre-loads the form and saves via PUT instead of POST.
  const { id: editId } = useParams();
  const isEdit = !!editId;
  const [meta, setMeta] = useState(null);
  const [metaErr, setMetaErr] = useState(null);

  // Form state — defaults seeded from the "extraction" template so a
  // first-load user can save & try immediately. Switching task type
  // re-applies the matching template (see useEffect below).
  const initial = TEMPLATES.extraction;
  const [name, setName] = useState(initial.name);
  const [category, setCategory] = useState('Construction');
  const [description, setDescription] = useState(initial.description);
  const [taskType, setTaskType] = useState('extraction');
  // Free-text label when task_type === 'custom' (e.g. "RFI Drafter").
  const [customTaskLabel, setCustomTaskLabel] = useState('');
  const [personaMd, setPersonaMd] = useState(initial.personaMd);
  const [strategies, setStrategies] = useState(
    initial.strategies.map((s) => ({ ...s, steps: s.steps || '' }))
  );
  const [evaluatorBuiltIn, setEvaluatorBuiltIn] = useState('rowCountRatioEvaluator');
  const [minScoreOverride, setMinScoreOverride] = useState('');
  // Only used when evaluatorBuiltIn === 'customPromptEvaluator'. The
  // server saves it under evaluator_config.custom_prompt and feeds it
  // to an LLM-as-judge call at evaluation time.
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedTools, setSelectedTools] = useState(new Set(initial.tools));
  const [suggestedQueries, setSuggestedQueries] = useState(initial.suggestedQueries.slice());

  // Tracks whether we should reapply the template when task type
  // changes. After the first user edit to a template-driven field
  // (name / persona / strategies / suggestedQueries), task-type
  // changes only update the evaluator and tool list — we don't blow
  // away the user's text. Reset via the "Reset to template" button.
  const [templateLocked, setTemplateLocked] = useState(false);

  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState(null);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiErr, setAiErr] = useState(null);
  // Live elapsed counter shown while the AI is filling — Sonnet takes
  // ~15-30s for a complete draft, so a silent spinner feels broken.
  // The counter resets to 0 on each click and updates once per second.
  const [aiElapsed, setAiElapsed] = useState(0);
  useEffect(() => {
    if (!aiSuggesting) return;
    setAiElapsed(0);
    const start = Date.now();
    const id = setInterval(() => setAiElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [aiSuggesting]);

  /**
   * Ask the backend to fill every field from the user's name + optional
   * description. Server calls Sonnet with the field schemas; we pour
   * the response into form state. The user still has to review + save.
   * Locks the template so subsequent task-type swaps don't blow away
   * the AI's suggestions.
   */
  const onAiSuggest = async () => {
    if (!name.trim() && !description.trim()) {
      setAiErr('Type an agent name or one-line description first.');
      return;
    }
    setAiSuggesting(true); setAiErr(null);
    try {
      const r = await fetch('/api/rsi-agents/draft', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), description: description.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
      const d = data.draft || {};
      // Apply every field — overwriting is the point. The user kicked
      // this off explicitly, they expect the form to change.
      if (d.name) setName(String(d.name).slice(0, 80));
      if (d.category) setCategory(String(d.category));
      if (d.description) setDescription(String(d.description).slice(0, 500));
      if (d.task_type) setTaskType(String(d.task_type));
      if (d.custom_task_label) setCustomTaskLabel(String(d.custom_task_label).slice(0, 80));
      if (d.persona_md) setPersonaMd(String(d.persona_md).slice(0, 8000));
      if (Array.isArray(d.strategies) && d.strategies.length > 0) {
        setStrategies(d.strategies.map((s) => ({
          name: String(s.name || '').slice(0, 80),
          when: String(s.when || '').slice(0, 200),
          steps: Array.isArray(s.steps) ? s.steps.join('\n') : (s.steps || ''),
          exit: String(s.exit || '').slice(0, 200),
        })));
      }
      if (d.evaluator_built_in) setEvaluatorBuiltIn(String(d.evaluator_built_in));
      if (d.custom_prompt) setCustomPrompt(String(d.custom_prompt).slice(0, 4000));
      if (Array.isArray(d.tools)) setSelectedTools(new Set(d.tools));
      if (Array.isArray(d.suggested_queries) && d.suggested_queries.length > 0) {
        setSuggestedQueries(d.suggested_queries.slice(0, 10).map((q) => String(q).slice(0, 200)));
      }
      setTemplateLocked(true);  // AI fill is the user's intent; don't swap on type change
    } catch (e) {
      setAiErr(e.message);
    } finally {
      setAiSuggesting(false);
    }
  };

  // Fetch dropdown metadata on mount
  useEffect(() => {
    fetch('/api/rsi-agents/meta', { credentials: 'include' })
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(setMeta)
      .catch((e) => setMetaErr(e.message));
  }, []);

  // Edit mode: prefill every field from the existing agent. Lock the
  // template so changing task type doesn't overwrite the user's prior
  // work. If the GET fails (deleted, not authorized) bounce to the
  // list page with an error.
  useEffect(() => {
    if (!editId) return;
    let cancelled = false;
    fetch(`/api/rsi-agents/${encodeURIComponent(editId)}`, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw new Error(body.error || `HTTP ${r.status}`);
        }
        return r.json();
      })
      .then(({ agent: a }) => {
        if (cancelled || !a) return;
        setName(a.name || '');
        setCategory(a.category || 'Construction');
        setDescription(a.description || '');
        setTaskType(a.task_type || 'extraction');
        setCustomTaskLabel(a.custom_task_label || '');
        setPersonaMd(a.persona_md || '');
        setStrategies(
          (a.strategies_json && a.strategies_json.length > 0
            ? a.strategies_json
            : [{ name: '', when: '', steps: [], exit: '' }]
          ).map((s) => ({
            name: s.name || '',
            when: s.when || '',
            steps: Array.isArray(s.steps) ? s.steps.join('\n') : (s.steps || ''),
            exit: s.exit || '',
          }))
        );
        const ec = a.evaluator_config || {};
        setEvaluatorBuiltIn(ec.built_in || 'rowCountRatioEvaluator');
        setMinScoreOverride(
          ec.overrides && typeof ec.overrides.minScore === 'number'
            ? String(ec.overrides.minScore)
            : ''
        );
        setCustomPrompt(ec.custom_prompt || '');
        setSelectedTools(new Set(Array.isArray(a.tools_json) ? a.tools_json : []));
        setSuggestedQueries(
          Array.isArray(a.suggested_queries) && a.suggested_queries.length > 0
            ? a.suggested_queries.slice()
            : ['']
        );
        // The user is editing real values now — never auto-swap on
        // task type change.
        setTemplateLocked(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setSaveErr(`Couldn't load this agent for editing: ${e.message}`);
      });
    return () => { cancelled = true; };
  }, [editId]);

  // Auto-apply the template for the chosen task type. If the user has
  // edited any text fields (templateLocked=true), only update the
  // evaluator + tool whitelist; preserve their text.
  const TASK_TO_EVAL = {
    extraction: 'rowCountRatioEvaluator',
    estimation: 'rowCountRatioEvaluator',
    compliance: 'citationCoverageEvaluator',
    submittal: 'checklistCoverageEvaluator',
    drawing_review: 'sheetRefCoverageEvaluator',
  };
  useEffect(() => {
    // 'custom' has no canonical template — the user is going off-script,
    // so we don't auto-fill anything. Default the evaluator to the
    // LLM-as-judge option since custom jobs usually need custom rules.
    if (taskType === 'custom') {
      if (!templateLocked) {
        setEvaluatorBuiltIn('customPromptEvaluator');
      }
      return;
    }
    const t = TEMPLATES[taskType];
    if (!t) return;
    setEvaluatorBuiltIn(TASK_TO_EVAL[taskType] || 'rowCountRatioEvaluator');
    setSelectedTools(new Set(t.tools));
    if (!templateLocked) {
      setName(t.name);
      setDescription(t.description);
      setPersonaMd(t.personaMd);
      setStrategies(t.strategies.map((s) => ({ ...s, steps: s.steps || '' })));
      setSuggestedQueries(t.suggestedQueries.slice());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskType]);

  // Mark the form as "locked" (user has edited) so we stop blowing
  // away their work when they switch task types. Any text-input
  // change calls touch().
  const touch = () => { if (!templateLocked) setTemplateLocked(true); };
  const resetTemplate = () => {
    const t = TEMPLATES[taskType];
    if (!t) return;
    setName(t.name);
    setDescription(t.description);
    setPersonaMd(t.personaMd);
    setStrategies(t.strategies.map((s) => ({ ...s, steps: s.steps || '' })));
    setSelectedTools(new Set(t.tools));
    setSuggestedQueries(t.suggestedQueries.slice());
    setEvaluatorBuiltIn(TASK_TO_EVAL[taskType] || 'rowCountRatioEvaluator');
    setMinScoreOverride('');
    setTemplateLocked(false);
  };

  // ── Strategies (dynamic list) ─────────────────────────────────
  const addStrategy = () => setStrategies((prev) => [
    ...prev, { name: '', when: '', steps: '', exit: '' },
  ]);
  const removeStrategy = (i) => setStrategies((prev) => prev.filter((_, idx) => idx !== i));
  const updateStrategy = (i, field, val) => {
    touch();
    setStrategies((prev) => prev.map((s, idx) => idx === i ? { ...s, [field]: val } : s));
  };

  // ── Tools (set toggle) ────────────────────────────────────────
  const toggleTool = (name) => setSelectedTools((prev) => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  // ── Suggested queries (dynamic) ───────────────────────────────
  const addQuery = () => setSuggestedQueries((prev) => [...prev, '']);
  const removeQuery = (i) => setSuggestedQueries((prev) => prev.filter((_, idx) => idx !== i));
  const updateQuery = (i, val) => {
    touch();
    setSuggestedQueries((prev) => prev.map((q, idx) => idx === i ? val : q));
  };

  // ── Submit ────────────────────────────────────────────────────
  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setSaveErr(null);
    try {
      // Map but DO NOT filter — previously the client dropped any
      // strategy missing name/when/exit before sending. That made the
      // server respond with the misleading "at least one strategy is
      // required" instead of the specific "strategy[N] needs name,
      // when, exit" error. Now we surface the specific gap.
      const normalizedStrategies = strategies.map((s) => ({
        name: s.name.trim(),
        when: s.when.trim(),
        steps: s.steps.split('\n').map((l) => l.trim()).filter(Boolean),
        exit: s.exit.trim(),
      }));
      // Client-side pre-check so the user sees a clear inline error
      // before the round trip.
      const incomplete = [];
      normalizedStrategies.forEach((s, i) => {
        const missing = [];
        if (!s.name) missing.push('play name');
        if (!s.when) missing.push('when to use');
        if (!s.exit) missing.push("how the agent knows it's done");
        if (missing.length) incomplete.push(`Play ${i + 1} is missing: ${missing.join(', ')}.`);
      });
      if (normalizedStrategies.length === 0) {
        throw new Error('Add at least one play in section 3.');
      }
      if (incomplete.length) {
        throw new Error(incomplete.join(' '));
      }
      if (evaluatorBuiltIn === 'customPromptEvaluator' && !customPrompt.trim()) {
        throw new Error("Section 4: write the quality rule you want the agent to check itself against.");
      }

      if (taskType === 'custom' && !customTaskLabel.trim()) {
        throw new Error('Section 1: when picking "Something else", add a short label naming the job (e.g. "RFI Drafting Assistant").');
      }

      const payload = {
        name: name.trim(),
        category, description: description.trim(),
        task_type: taskType,
        custom_task_label: taskType === 'custom' ? customTaskLabel.trim() : null,
        persona_md: personaMd.trim(),
        strategies: normalizedStrategies,
        evaluator_config: {
          built_in: evaluatorBuiltIn,
          overrides: minScoreOverride
            ? { minScore: Number(minScoreOverride) }
            : {},
          ...(evaluatorBuiltIn === 'customPromptEvaluator'
            ? { custom_prompt: customPrompt.trim() }
            : {}),
        },
        tools: [...selectedTools],
        suggested_queries: suggestedQueries.map((q) => q.trim()).filter(Boolean),
      };
      const url = isEdit
        ? `/api/rsi-agents/${encodeURIComponent(editId)}`
        : '/api/rsi-agents';
      const r = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await r.json();
      if (!r.ok) {
        const msg = data.details ? `${data.error}: ${data.details.join('; ')}` : (data.error || `HTTP ${r.status}`);
        throw new Error(msg);
      }
      navigate(`/jobotics-agent?agentId=${encodeURIComponent(data.agent.id)}`);
    } catch (e2) {
      setSaveErr(e2.message);
    } finally {
      setSaving(false);
    }
  };

  if (metaErr) {
    return <div className="max-w-3xl mx-auto p-8 text-rose-700">Failed to load builder metadata: {metaErr}</div>;
  }
  if (!meta) {
    return <div className="max-w-3xl mx-auto p-8 text-slate-500">Loading builder…</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            {isEdit ? 'Edit your agent' : 'Build your own agent'}
          </h1>
          {isEdit ? (
            <p className="text-slate-600 mt-2 text-sm">
              Update any field and save. Your changes apply immediately — anyone running this agent next will see the new behavior. The agent's ID is unchanged, so existing pin/sidebar links keep working.
            </p>
          ) : (
            <>
              <p className="text-slate-600 mt-2 text-sm">
                Every field is pre-filled with a realistic starter for the job type you pick. Tweak what doesn't fit, then hit <strong>Save &amp; try it out</strong> — often you only need to change a sentence or two.
              </p>
              <p className="text-slate-500 mt-1 text-xs">
                Shared with everyone on your team by default. Anyone here can use any agent — no install, no setup.
              </p>
            </>
          )}
        </div>
        {!isEdit && (
          <button type="button" onClick={resetTemplate}
                  title="Replace every field with the starter template for the selected job type"
                  className="shrink-0 text-xs text-teal-700 hover:text-teal-900 hover:underline whitespace-nowrap pt-2">
            Reset to template
          </button>
        )}
      </header>

      <form onSubmit={onSubmit} className="space-y-8">
        {/* AI Quick-fill banner — type a name, click the button, the
            entire form fills with a realistic MEP-domain agent matching
            that name. ~1¢-2¢ per call (Sonnet). Hidden in edit mode
            because the form is already populated from the existing
            agent and AI rewriting would destroy the user's edits. */}
        {!isEdit && (
          <section className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 flex items-center justify-between gap-4">
            <div className="text-sm">
              <div className="font-medium text-indigo-900 flex items-center gap-1.5">
                <span aria-hidden>✨</span> Quick-fill with AI
              </div>
              <div className="text-xs text-indigo-800/80 mt-0.5">
                Type a name in Section 1 (and optionally a one-line description), then click here. The AI fills every remaining field with a realistic MEP starter — you review and tweak. Typically takes 15-30 seconds.
              </div>
              {aiSuggesting && (
                <div className="text-xs text-indigo-700 mt-1 font-medium tabular-nums">
                  Working… {aiElapsed}s elapsed (usually 15-30s)
                </div>
              )}
              {aiErr && (
                <div className="text-xs text-rose-700 mt-1">{aiErr}</div>
              )}
            </div>
            <button
              type="button"
              onClick={onAiSuggest}
              disabled={aiSuggesting}
              className="shrink-0 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 text-sm font-medium shadow-sm disabled:opacity-50 flex items-center gap-2"
            >
              {aiSuggesting ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4"/>
                    <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/>
                  </svg>
                  <span className="tabular-nums">Filling… {aiElapsed}s</span>
                </>
              ) : (
                <>✨ Suggest fields</>
              )}
            </button>
          </section>
        )}

        {/* ── Section 1: Basics ── */}
        <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">1. Name your agent</h2>
          <p className="text-xs text-slate-500 mb-4">What should this agent be called, and which folder does it live in?</p>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Agent name *" hint='Use plain English — what would a foreman call it? e.g. "HVAC Submittal Reviewer"'>
              <input type="text" required value={name} onChange={(e) => { setName(e.target.value); touch(); }}
                     maxLength={80} className={inputClass} placeholder="e.g. HVAC Submittal Reviewer" />
            </Field>
            <Field label="Folder" hint="Where it shows up in the agent browser.">
              <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
                {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          <Field label="One-line summary" hint="What does this agent do? Shown on the card in the browser.">
            <input type="text" value={description} onChange={(e) => { setDescription(e.target.value); touch(); }}
                   maxLength={500} className={inputClass}
                   placeholder='e.g. "Reviews HVAC submittals against ASHRAE 90.1 with page-cited findings."' />
          </Field>

          <Field label="What job will this agent do? *" hint="Pick the closest match — we set sensible defaults for the rest. Pick &quot;Something else&quot; if your job doesn't fit and you'll write your own quality rule.">
            <div className="flex flex-wrap gap-2">
              {meta.taskTypes.map((t) => (
                <label key={t} className={`px-3 py-1.5 rounded border text-sm cursor-pointer transition ${taskType === t ? 'bg-teal-600 text-white border-teal-600' : 'bg-white border-slate-300 hover:border-slate-400'}`}>
                  <input type="radio" name="taskType" value={t} checked={taskType === t}
                         onChange={() => setTaskType(t)} className="sr-only" />
                  {TASK_TYPE_LABELS[t] || t}
                </label>
              ))}
            </div>
          </Field>

          {/* Custom-task label: only shown when "Something else" is picked.
              Persists as the agent's user-visible job name in lists. */}
          {taskType === 'custom' && (
            <Field
              label="Short label for this custom job *"
              hint='e.g. "RFI Drafting Assistant", "Closeout Document Auditor", "Daily Log Summarizer". Shown wherever the standard job type usually appears.'
            >
              <input type="text" value={customTaskLabel}
                     onChange={(e) => { setCustomTaskLabel(e.target.value); touch(); }}
                     maxLength={80} className={inputClass}
                     placeholder='e.g. "Submittal Cover Sheet Generator"' />
            </Field>
          )}
        </section>

        {/* ── Section 2: Persona ── */}
        <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">2. How should it think?</h2>
          <p className="text-xs text-slate-500 mb-4">
            Describe the agent's job and the must-follow rules — like you'd brief a new hire. A few sentences on what you want, then a bulleted list of "always do this / never do that." This is what makes the agent yours.
          </p>
          <textarea
            value={personaMd} onChange={(e) => { setPersonaMd(e.target.value); touch(); }}
            rows={10} maxLength={8000}
            placeholder={`What this agent does\n\nReviews HVAC submittals against the ASHRAE 90.1 energy code. Input is a submittal PDF (single or multi-section). Output is a finding list — each row has the page in the document AND the code clause that applies.\n\nMust-follow rules\n- Never call something "compliant" without citing both (1) the document page and (2) the specific ASHRAE clause.\n- If a clause has an exception, call it out explicitly.\n- If a piece of equipment isn't covered by 90.1, say "out of scope" — don't guess.`}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500"
          />
        </section>

        {/* ── Section 3: Strategies ── */}
        <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">3. Spell out the playbook</h2>
          <p className="text-xs text-slate-500 mb-4">
            List 1-3 ways this agent should attack the job. Each "play" has a name, when to use it, the steps in order, and how it knows it's done. The agent will pick the right play for each file.
          </p>
          <div className="space-y-4">
            {strategies.map((s, i) => (
              <div key={i} className="rounded border border-slate-200 p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold text-slate-700">Play {i + 1}</div>
                  {strategies.length > 1 && (
                    <button type="button" onClick={() => removeStrategy(i)}
                            className="text-xs text-rose-600 hover:text-rose-800">Remove</button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Play name" hint='Short label. e.g. "ASHRAE check" or "Electrical takeoff"'>
                    <input value={s.name} onChange={(e) => updateStrategy(i, 'name', e.target.value)}
                           className={inputClass} placeholder="primary play" />
                  </Field>
                  <Field label="When to use this play" hint='e.g. "the file is an electrical submittal" or "the user mentions ASHRAE"'>
                    <input value={s.when} onChange={(e) => updateStrategy(i, 'when', e.target.value)}
                           className={inputClass} placeholder="default case" />
                  </Field>
                </div>
                <Field label="Steps (one per line, in order)" hint="What does the agent do, top to bottom? Use plain English.">
                  <textarea rows={4} value={s.steps}
                            onChange={(e) => updateStrategy(i, 'steps', e.target.value)}
                            placeholder={'Search the document for ASHRAE clause terms\nRead the pages near each match for context\nFor each requirement, classify as compliant / non-compliant / not-applicable\nFor every finding, write down the document page AND the code clause'}
                            className={`${inputClass} text-xs`} />
                </Field>
                <Field label='How does the agent know it&apos;s done?' hint='Use a concrete check — "every line has labor hours" or "every finding has a page citation."'>
                  <input value={s.exit} onChange={(e) => updateStrategy(i, 'exit', e.target.value)}
                         className={inputClass} placeholder='e.g. "every finding has both a document page AND an ASHRAE clause citation"' />
                </Field>
              </div>
            ))}
            {strategies.length < 6 && (
              <button type="button" onClick={addStrategy}
                      className="text-sm text-teal-700 hover:text-teal-900 font-medium">
                + Add another play
              </button>
            )}
          </div>
        </section>

        {/* ── Section 4: Evaluator ── */}
        <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">4. How should the agent check its work?</h2>
          <p className="text-xs text-slate-500 mb-4">
            Pick the kind of quality check that fits this job. The agent grades itself before showing you the result — if the grade is low, it tries again. Default grade-to-pass is 80 out of 100.
          </p>
          <Field label="Quality check">
            <select value={evaluatorBuiltIn} onChange={(e) => { setEvaluatorBuiltIn(e.target.value); touch(); }} className={inputClass}>
              {meta.evaluators.map((e) => (
                <option key={e.name} value={e.name}>{EVALUATOR_LABELS[e.name] || e.name}</option>
              ))}
            </select>
          </Field>

          {/* Custom check — only shown when the LLM-as-judge option is
              picked. The user writes their quality rule in plain
              English; at runtime the platform feeds it to an LLM with
              the agent's output and parses a {score, verdict, gaps,
              suggestion} response. ~1¢-3¢ per evaluation. */}
          {evaluatorBuiltIn === 'customPromptEvaluator' && (
            <Field
              label="Your quality rule *"
              hint='Write what "done well" means for this agent, in plain English. Be specific — name columns, give numeric ranges, list required keywords. The agent grades itself against this rule on every run.'
            >
              <textarea
                value={customPrompt}
                onChange={(e) => { setCustomPrompt(e.target.value); touch(); }}
                rows={6}
                maxLength={4000}
                placeholder={`e.g. "The agent's output should be an Excel with every NECA labor unit from the source. For each row, Labor Hrs/Unit must be a positive number AND Description must include the conduit size. Score 100 only if (1) total rows are within 5% of the source's stated count, (2) no row has missing Description, (3) the totals line at the bottom sums to a positive number, AND (4) at least 95% of rows have non-empty Difficult and Very Difficult columns. Penalize 20 points per missing condition."`}
                className={`${inputClass} text-sm`}
              />
              <div className="mt-1 text-xs text-slate-500">
                Costs ~1¢-3¢ per evaluation (uses Claude Sonnet as the judge). Prefer the deterministic checks above when they cover your needs — they're free and faster.
              </div>
            </Field>
          )}

          <Field label="Make it stricter (optional)" hint='Want the agent to retry until it scores even higher? Default passing grade is 80. Raise it for safety-critical work (e.g. 90 for code compliance).'>
            <input type="number" min="0" max="100" value={minScoreOverride}
                   onChange={(e) => { setMinScoreOverride(e.target.value); touch(); }}
                   className={inputClass} placeholder="80" />
          </Field>
        </section>

        {/* ── Section 5: Tools ── */}
        <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">5. What can the agent use?</h2>
          <p className="text-xs text-slate-500 mb-4">
            Check the capabilities this agent needs. Defaults cover most document-reading jobs. Add things like labor estimators if your agent does takeoffs, or external code lookups if it does compliance work.
          </p>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {meta.tools.map((t) => (
              <label key={t.name} className="flex items-center text-sm py-1">
                <input type="checkbox" checked={selectedTools.has(t.name)}
                       onChange={() => toggleTool(t.name)}
                       className="mr-2 rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
                <span className="text-xs">{TOOL_LABELS[t.name] || t.name}</span>
              </label>
            ))}
          </div>
          <div className="mt-2 text-xs text-slate-500">
            {selectedTools.size} of {meta.tools.length} capabilities turned on
          </div>
        </section>

        {/* ── Section 6: Suggested queries ── */}
        <section className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">6. Example questions for users</h2>
          <p className="text-xs text-slate-500 mb-4">
            Write 3-5 example questions a teammate could click to get started. These show up under your agent in the chat — they take the guesswork out of the first message.
          </p>
          <div className="space-y-2">
            {suggestedQueries.map((q, i) => (
              <div key={i} className="flex gap-2">
                <input value={q} onChange={(e) => updateQuery(i, e.target.value)}
                       className={`${inputClass} flex-1`} maxLength={200}
                       placeholder='e.g. "Check this submittal against ASHRAE 90.1."' />
                {suggestedQueries.length > 1 && (
                  <button type="button" onClick={() => removeQuery(i)}
                          className="text-xs text-rose-600 hover:text-rose-800 px-2">Remove</button>
                )}
              </div>
            ))}
            {suggestedQueries.length < 10 && (
              <button type="button" onClick={addQuery}
                      className="text-sm text-teal-700 hover:text-teal-900 font-medium">
                + Add query
              </button>
            )}
          </div>
        </section>

        {/* Submit */}
        {saveErr && (
          <div className="rounded-md bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 text-sm">
            {saveErr}
          </div>
        )}
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={() => navigate('/rsi-agents')}
                  className="text-sm text-slate-700 hover:text-slate-900 px-4 py-2">
            Cancel
          </button>
          <button type="submit" disabled={saving}
                  className="rounded-md bg-teal-600 hover:bg-teal-700 text-white px-6 py-2 text-sm font-medium shadow-sm disabled:opacity-50">
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Save & try it out')}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputClass = 'w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500';

function Field({ label, hint, children }) {
  return (
    <div className="mt-3">
      <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
      {hint && <div className="text-xs text-slate-500 mb-1">{hint}</div>}
      {children}
    </div>
  );
}
